import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * User management for the Admin Panel — list, role updates (super admin),
 * and the "manage this user's entity-admin slots" modal.
 *
 * Cross-hook wiring: `refetchEntities` is called after role/entity changes
 * because verified-by/entity_admins data on entity rows may have changed.
 * `refetchStats` updates the user-count counter on the dashboard tab.
 */
export function useAdminUsers({ token, activeTab, isAdmin, refetchEntities, refetchStats }) {
  const [users, setUsers] = useState([]);
  // Lightweight entities list — used to populate the "attach to partners"
  // picker in the Add User dialog. Fetched once when the Users tab opens so
  // the dialog opens instantly. The backend already scopes this list for
  // entity admins, so we don't need to re-scope client-side.
  const [attachableEntities, setAttachableEntities] = useState([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);

  // Role modal
  const [showUserRoleModal, setShowUserRoleModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // User-entities modal
  const [showUserEntitiesModal, setShowUserEntitiesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserEntities, setSelectedUserEntities] = useState([]);
  const [allEntities, setAllEntities] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchAttachableEntities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/entities?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAttachableEntities(response.data.entities || []);
    } catch (error) {
      console.error('Error fetching attachable entities:', error);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'users' && isAdmin) {
      fetchUsers();
      fetchAttachableEntities();
    }
  }, [activeTab, isAdmin, fetchUsers, fetchAttachableEntities]);

  // ---------- Role ----------
  const openUserRoleModal = (userRow) => {
    setEditingUser(userRow);
    setShowUserRoleModal(true);
  };

  const handleUpdateUserRole = async (userId, isAdminFlag, adminRole = null) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        { is_admin: isAdminFlag, admin_role: adminRole },
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      toast.success('User role updated successfully');
      setShowUserRoleModal(false);
      setEditingUser(null);
      fetchUsers();
      refetchStats?.();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  // ---------- User-Entities ----------
  const openUserEntitiesModal = async (userRow) => {
    setSelectedUser(userRow);
    setShowUserEntitiesModal(true);
    try {
      const response = await axios.get(`${API}/admin/entities?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllEntities(response.data.entities || []);
      const userEntities = response.data.entities
        .filter((e) => e.entity_admins && e.entity_admins.includes(userRow.user_id))
        .map((e) => e.entity_id);
      setSelectedUserEntities(userEntities);
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast.error('Failed to load entities');
    }
  };

  const toggleUserEntity = (entityId) => {
    setSelectedUserEntities((prev) =>
      prev.includes(entityId) ? prev.filter((id) => id !== entityId) : [...prev, entityId]
    );
  };

  const closeUserEntitiesModal = () => {
    setShowUserEntitiesModal(false);
    setSelectedUser(null);
    setSelectedUserEntities([]);
    setAllEntities([]);
  };

  const handleSaveUserEntities = async () => {
    if (!selectedUser) {
      toast.error('No user selected');
      return;
    }
    try {
      const response = await axios.put(
        `${API}/admin/users/${selectedUser.user_id}/entities`,
        selectedUserEntities,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      const data = response.data;
      if (data.skipped_count > 0) {
        toast.warning(`${data.message}. ${data.skipped_count} entities skipped (already have 2 admins).`);
      } else {
        toast.success(data.message || 'User entities updated successfully');
      }
      closeUserEntitiesModal();
      fetchUsers();
      refetchEntities?.();
    } catch (error) {
      console.error('Error updating user entities:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user entities');
    }
  };

  // ---------- Delete ----------
  // Backend cascades: purges saved_routes, reviews, events, device_tokens,
  // user_sessions for the target user and pulls their id from entity admin
  // arrays. See admin_delete_user / admin_bulk_delete_users in routes/admin.py.
  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User deleted');
      fetchUsers();
      refetchStats?.();
      refetchEntities?.();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleBulkDeleteUsers = async (userIds) => {
    try {
      const res = await axios.post(
        `${API}/admin/users/bulk-delete`,
        { user_ids: userIds },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      const { deleted = [], skipped = [] } = res.data || {};
      if (deleted.length) toast.success(`Deleted ${deleted.length} user(s)`);
      if (skipped.length) {
        // Show why each was skipped so admins understand what happened.
        const reasons = skipped.map((s) => `• ${s.user_id}: ${s.reason}`).join('\n');
        toast.warning(`Skipped ${skipped.length}:\n${reasons}`);
      }
      fetchUsers();
      refetchStats?.();
      refetchEntities?.();
      return res.data;
    } catch (error) {
      console.error('Error bulk-deleting users:', error);
      toast.error(error.response?.data?.detail || 'Failed to bulk delete users');
      return null;
    }
  };

  return {
    users,
    fetchUsers,
    // add-user dialog
    showAddUserDialog,
    setShowAddUserDialog,
    attachableEntities,
    fetchAttachableEntities,
    // role modal
    showUserRoleModal,
    setShowUserRoleModal,
    editingUser,
    openUserRoleModal,
    handleUpdateUserRole,
    // user-entities modal
    showUserEntitiesModal,
    selectedUser,
    selectedUserEntities,
    allEntities,
    openUserEntitiesModal,
    toggleUserEntity,
    handleSaveUserEntities,
    closeUserEntitiesModal,
    // delete
    handleDeleteUser,
    handleBulkDeleteUsers,
  };
}
